<template>
  <focus-trap :clickDeactivates="false" :escapeDeactivates="false">
    <div
      class="measurement-result-edit-popup"
      :tabindex="`-${layerNumber}`"
      @keyup.esc.stop="handleClose"
    >
      <validation-observer v-slot="{ invalid }">
        <popup
          :buttons="popupButtons"
          :content-style="{
            maxHeight: '650px',
            marginTop: '25px',
            padding: '0 30px'
          }"
          :disabled-flg="isUpdateDisabled(invalid)"
          :layer-number="layerNumber"
          @cancel="handleClose"
          @click-close-mark="handleClose"
          @close="handleClose"
          @decision="checkSpeciesThenUpdate"
        >
          <template v-slot:content>
            <div class="content">
              <div class="header">
                <div class="title">{{ popupTitle }}</div>
                <base-button-small-red
                  v-if="showDeleteButton"
                  data-test="delete-button"
                  :disabled="isDeleteDisabled()"
                  @click="openDeletePopup"
                  >削除</base-button-small-red
                >
              </div>
              <div class="input-form">
                <div class="patient">
                  <measurement-result-patient-detail
                    data-test="measurement-result-patient-detail"
                    :patient="patient"
                    :run-date="runDate"
                  />
                </div>
                <div class="measurement-result">
                  <measurement-result-input-table
                    data-test="measurement-result-input-table"
                    :measurement-result="measurementResult"
                    :measurement-result-items="measurementResultItems"
                    :nx-measurement-result="nxMeasurementResult"
                    :patient="patient"
                    @input-measurement-result="inputMeasurementResult"
                    @input-measurement-result-item="inputMeasurementResultItem"
                    @input-measurement-result-items="
                      inputMeasurementResultItems
                    "
                  />
                </div>
              </div>
            </div>
          </template>
        </popup>
      </validation-observer>
      <base-loading
        data-test="base-loading"
        :wait-flg="isCreatedHook || isDeleting || isUpdating"
      />
      <announce-popup
        v-if="confirmationPopup.isOpened"
        data-test="popup confirmation"
        :buttons="confirmationPopup.buttons"
        :layer-number="confirmationPopup.layerNumber"
        :title="confirmationPopup.title"
        :type="confirmationPopup.type"
        @cancel="closeAnnouncePopup(confirmationPopup)"
        @decision="closeSelf"
        >{{ confirmationPopup.message }}</announce-popup
      >
      <announce-popup
        v-if="deletePopup.isOpened"
        data-test="popup delete"
        :buttons="deletePopup.buttons"
        :disabled="isDeleting"
        :layer-number="deletePopup.layerNumber"
        :title="deletePopup.title"
        :type="deletePopup.type"
        @cancel="closeAnnouncePopup(deletePopup)"
        @decision="deleteMeasurementResult"
        >{{ deletePopup.message }}</announce-popup
      >
      <announce-popup
        v-if="resultPopup.isOpened"
        data-test="popup result"
        :buttons="resultPopup.buttons"
        :layer-number="resultPopup.layerNumber"
        :title="resultPopup.title"
        :type="resultPopup.type"
        @close="closeResultPopup"
        >{{ resultPopup.message }}</announce-popup
      >
      <announce-popup
        v-if="speciesAlertPopup.isOpened"
        data-test="popup species"
        :buttons="speciesAlertPopup.buttons"
        :disabled="isUpdating"
        :layer-number="speciesAlertPopup.layerNumber"
        :title="speciesAlertPopup.title"
        :type="speciesAlertPopup.type"
        @cancel="closeAnnouncePopup(speciesAlertPopup)"
        @decision="updateMeasurementResult"
        >{{ speciesAlertPopup.message }}</announce-popup
      >
    </div>
  </focus-trap>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import BaseButtonSmallRed from '@/components/parts/atoms/BaseButtonSmallRed'
import BaseLoading from '@/components/parts/atoms/BaseLoading'
import CheckPopupInputDifference from '@/components/mixins/CheckPopupInputDifference'
import MeasurementResultInputTable from '@/components/parts/organisms/MeasurementResultInputTable'
import MeasurementResultPatientDetail from '@/components/parts/molecules/MeasurementResultPatientDetail'
import Popup from '@/components/popups/Popup'
import _ from 'lodash'
import dedent from 'dedent'
import { FocusTrap } from 'focus-trap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters } from 'vuex'
import {
  toItemDefaultValues,
  useSettingDefaultValues
} from '@/utils/measurement_utils'

export default {
  name: 'MeasurementResultEditPopup',

  components: {
    AnnouncePopup,
    BaseButtonSmallRed,
    BaseLoading,
    FocusTrap,
    MeasurementResultPatientDetail,
    MeasurementResultInputTable,
    Popup,
    ValidationObserver
  },

  mixins: [CheckPopupInputDifference],

  props: {
    canDelete: { type: Boolean, default: false },
    isPatientLinked: { type: Boolean, default: true },
    layerNumber: { type: Number, default: 1 },
    measurementResultId: { type: Number, default: 0 },
    patientId: { type: Number, default: 0 },
    shouldFetchData: { type: Boolean, default: false }
  },

  data() {
    return {
      confirmationPopup: {
        buttons: ['キャンセル', '閉じる'],
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        message: '入力内容を保存せずに閉じようとしています。\nよろしいですか？',
        title: '確認',
        type: 'alert'
      },
      deletePopup: {
        buttons: ['削除しない', '削除する'],
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        message: '削除してもよろしいですか？',
        title: '確認',
        type: 'alert'
      },
      initialData: {},
      inputData: {},
      isCreatedHook: false,
      isDeleting: false,
      isUpdating: false,
      measurementResult: {},
      measurementResultItems: [],
      nxMeasurementResult: null,
      patient: null,
      patientSpecies: null,
      resultPopup: {
        buttons: ['閉じる'],
        isOpened: false,
        layerNumber: this.layerNumber + 2,
        message: '',
        title: '',
        type: ''
      },
      shouldCloseSelf: false,
      shouldCloseParent: false,
      speciesAlertPopup: {
        buttons: ['キャンセル'],
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        message: '',
        title: '確認',
        type: 'alert'
      }
    }
  },

  computed: {
    ...mapGetters({
      getLaboratoryDefaultValueSetting:
        'laboratoryDefaultValueSettings/getLaboratoryDefaultValueSetting',
      getLaboratoryItem: 'laboratoryItems/getLaboratoryItem',
      getLaboratoryReferenceRange:
        'laboratoryReferenceRanges/getLaboratoryReferenceRange',
      getMeasurementResult: 'measurementResults/getDataById',
      getMeasurementResultItems:
        'measurementResultItems/getDataByMeasurementResultId',
      getNxMeasurementResult:
        'nxMeasurementResults/getDatumByMeasurementResultId',
      getSpecies: 'species/getDataById',
      getPatient: 'patients/getDataById',
      lookOnlyFlg: 'auth/lookOnlyFlg'
    }),
    actions() {
      return this.isPatientLinked
        ? { button: '登録', text: '編集' }
        : { button: '取込', text: '取込' }
    },
    diffFlg() {
      return !_.isEqual(this.initialData, this.inputData)
    },
    patientSpeciesCode() {
      return this.patientSpecies?.masterLaboratorySpeciesCode ?? ''
    },
    popupButtons() {
      return this.lookOnlyFlg ? ['閉じる'] : ['閉じる', this.actions.button]
    },
    popupTitle() {
      return `検査結果${this.actions.text}`
    },
    resultSpeciesCode() {
      return this.measurementResult?.masterLaboratorySpeciesCode || ''
    },
    runDate() {
      return this.measurementResult?.date || ''
    },
    showDeleteButton() {
      return this.canDelete && !this.lookOnlyFlg
    }
  },

  async created() {
    this.isCreatedHook = true
    if (this.shouldFetchData) {
      await this.$store.dispatch(
        'measurementResultItems/fetchDataByMeasurementResultId',
        this.measurementResultId
      )
      await this.$store.dispatch(
        'nxMeasurementResults/fetchDataByMeasurementResultId',
        this.measurementResultId
      )
    }
    this.setPatientValues()
    this.setMeasurementResult()
    this.setMeasurementResultItems()
    this.setNxMeasurementResult()
    this.inputData = {
      measurementResult: this.measurementResult,
      measurementResultItems: this.measurementResultItems
    }
    this.initialData = _.cloneDeep(this.inputData)
    this.isCreatedHook = false
  },

  methods: {
    async checkSpeciesThenUpdate() {
      if (this.patientSpeciesCode === this.resultSpeciesCode) {
        await this.updateMeasurementResult()
      } else {
        this.openSpeciesAlertPopup()
      }
    },
    closeAnnouncePopup(announcePopup) {
      announcePopup.isOpened = false
    },
    closeResultPopup() {
      this.resultPopup.isOpened = false
      if (this.shouldCloseSelf) {
        this.closeSelf()
      }
      if (this.shouldCloseParent) {
        this.$emit('close-parent')
      }
    },
    closeSelf() {
      this.$emit('close')
    },
    async deleteMeasurementResult() {
      this.isDeleting = true
      const result = await this.$store.dispatch(
        'measurementResults/delete',
        this.measurementResultId
      )
      if (result === 'success') {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '削除しました',
          title: '完了',
          type: 'success'
        }
        this.shouldCloseSelf = true
      } else {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '削除に失敗しました',
          title: '失敗',
          type: 'failure'
        }
        if (result === 'no data in clinic') {
          this.shouldCloseSelf = true
        }
      }
      this.isDeleting = false
    },
    getItemDefaultValues(itemCode) {
      const laboratoryItem = this.getLaboratoryItem(
        this.measurementResult.masterLaboratoryInstrumentCode,
        itemCode
      )
      const laboratoryItemRange = this.getLaboratoryReferenceRange(
        this.measurementResult.masterLaboratoryInstrumentCode,
        itemCode,
        this.measurementResult.masterLaboratorySpeciesCode,
        this.patient.birthday,
        this.measurementResult.date
      )
      const { unit, low, high } = toItemDefaultValues(
        laboratoryItem,
        laboratoryItemRange
      )
      return { unit, low, high }
    },
    handleClose() {
      if (this.diffFlg) {
        this.confirmationPopup.isOpened = true
      } else {
        this.closeSelf()
      }
    },
    inputMeasurementResult({ key, value }) {
      this.measurementResult[key] = value
    },
    inputMeasurementResultItem({ index, key, value }) {
      this.measurementResultItems[index][key] = value
    },
    inputMeasurementResultItems(measurementResultItems) {
      measurementResultItems.forEach((v, i) => {
        this.measurementResultItems.splice(i, 1, v)
      })
    },
    isDeleteDisabled() {
      return (
        !!this.lookOnlyFlg ||
        this.isCreatedHook ||
        this.isDeleting ||
        this.isUpdating
      )
    },
    isUpdateDisabled(invalid) {
      return (
        !!this.lookOnlyFlg ||
        this.isCreatedHook ||
        this.isDeleting ||
        this.isUpdating ||
        invalid
      )
    },
    openDeletePopup() {
      this.deletePopup.isOpened = true
    },
    openSpeciesAlertPopup() {
      const buttons = ['キャンセル', this.actions.button]
      const message = dedent`
        患者の検査動物種と検査結果の動物種が一致していません。
        ${this.actions.text}を行いますか？
      `
      this.speciesAlertPopup = {
        ...this.speciesAlertPopup,
        isOpened: true,
        buttons,
        message
      }
    },
    setMeasurementResult() {
      // created 時に使用
      // 患者情報は設定済み
      const measurementResult = this.getMeasurementResult(
        this.measurementResultId
      )
      const speciesCode = measurementResult.hasAssignedFlg
        ? measurementResult.masterLaboratorySpeciesCode
        : this.patientSpeciesCode
      this.measurementResult = {
        ...measurementResult,
        masterLaboratorySpeciesCode: speciesCode
      }
    },
    setMeasurementResultItems() {
      // created 時に使用
      // 患者情報 と measurementResult は設定済み
      const defaultValueSetting = this.getLaboratoryDefaultValueSetting(
        this.measurementResult.masterLaboratoryInstrumentCode
      )
      const makeCommonValues = resultItem => {
        return {
          id: resultItem.id,
          masterLaboratoryItemCode: resultItem.masterLaboratoryItemCode,
          qualifier: resultItem.qualifier,
          value: resultItem.value,
          dilutionRatio: resultItem.dilutionRatio
        }
      }
      const measurementResultItems = this.getMeasurementResultItems(
        this.measurementResultId
      )
      if (
        useSettingDefaultValues(defaultValueSetting, this.measurementResult)
      ) {
        this.measurementResultItems = measurementResultItems.map(v => {
          const { unit, low, high } = this.getItemDefaultValues(
            v.masterLaboratoryItemCode
          )
          return {
            ...makeCommonValues(v),
            unit: unit,
            referenceRangeLow: low,
            referenceRangeHigh: high
          }
        })
      } else {
        this.measurementResultItems = measurementResultItems.map(v => {
          return {
            ...makeCommonValues(v),
            unit: v.unit,
            referenceRangeLow: v.referenceRangeLow,
            referenceRangeHigh: v.referenceRangeHigh
          }
        })
      }
    },
    setNxMeasurementResult() {
      // created 時に使用
      const nxMeasurementResult = this.getNxMeasurementResult(
        this.measurementResultId
      )
      this.nxMeasurementResult = nxMeasurementResult
    },
    setPatientValues() {
      // created 時に使用
      const patient = this.getPatient(this.patientId)
      if (patient) {
        this.patient = patient
        const speciesId = patient.speciesId
        const patientSpecies = this.getSpecies(speciesId)
        if (patientSpecies) {
          this.patientSpecies = patientSpecies
        }
      }
    },
    async updateMeasurementResult() {
      this.isUpdating = true
      const action = this.actions.text
      const result = await this.$store.dispatch('measurementResults/update', {
        patientId: this.patientId,
        measurementResult: this.measurementResult,
        measurementResultItems: this.measurementResultItems
      })
      if (result === 'success') {
        this.initialData = _.cloneDeep(this.inputData)
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: `${action}に成功しました`,
          title: '完了',
          type: 'success'
        }
        this.shouldCloseSelf = true
      } else {
        let message = `${action}に失敗しました`
        if (result === 'no patient') {
          message = `${action}に失敗しました。\n対象の患者は既に削除されています。`
          this.shouldCloseSelf = true
          this.shouldCloseParent = true
        } else if (
          result === 'no data in clinic' ||
          result === 'no measurementResult'
        ) {
          message = `${action}に失敗しました。\n対象の検査結果は既に削除されています。`
          this.shouldCloseSelf = true
          this.shouldCloseParent = true
        } else if (result === 'patient already linked') {
          message = '取込に失敗しました。\n既に取込が完了しています。'
          this.shouldCloseSelf = true
        }
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message,
          title: '失敗',
          type: 'failure'
        }
      }
      this.isUpdating = false
    }
  }
}
</script>

<style lang="scss" scoped>
$header-height: 43px;
$patient-detail-height: 70px;

.measurement-result-edit-popup {
  position: absolute;
  top: 0;
  left: 0;
  .content {
    width: 790px;
    > .header {
      display: flex;
      justify-content: space-between;
      height: $header-height;
      position: sticky;
      top: 0;
      z-index: 200;
      background: #fff;
      > .title {
        text-align: left;
        font-size: 16px;
        font-weight: bold;
        color: #{$greyish-brown};
      }
    }
    > .input-form {
      > .patient {
        height: $patient-detail-height;
        position: sticky;
        top: $header-height;
        z-index: 200;
        background: #fff;
      }
      > .measurement-result {
        min-height: 300px;
      }
    }
  }
}
</style>
